import { useState, useCallback, useRef } from 'react';
import { type SystemRole, isAdmin } from '@peles/shared';
import { UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../context/DialogContext';
import { SplitPanelLayout } from '../components/layout/SplitPanelLayout';
import { useUsers, useDeactivateUser } from '../hooks/useUsers';
import {
  UserFilters,
  type UserFiltersState,
  UserTable,
  UserDetailPanel,
  CreateUserPanel,
} from '../components/users';

type PanelState = { mode: 'closed' } | { mode: 'edit'; userId: string } | { mode: 'create' };

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const { confirm } = useDialog();
  const [panel, setPanel] = useState<PanelState>({ mode: 'closed' });
  const [filters, setFilters] = useState<UserFiltersState>({
    search: '',
    role: '',
    isActive: 'all',
  });
  const hasUnsavedRef = useRef(false);

  // Build API filters (role + isActive)
  const apiFilters: { isActive?: boolean; systemRole?: SystemRole } = {};
  if (filters.role) apiFilters.systemRole = filters.role;
  if (filters.isActive !== 'all') apiFilters.isActive = filters.isActive === 'true';

  const { data: users = [], isLoading } = useUsers(
    Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
  );

  const deactivateUser = useDeactivateUser();

  const handleDeactivateUser = useCallback(
    (id: string) => {
      deactivateUser.mutate(id);
    },
    [deactivateUser],
  );

  // Client-side search filter
  const filteredUsers = filters.search
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          u.email.toLowerCase().includes(filters.search.toLowerCase()),
      )
    : users;

  const confirmUnsaved = useCallback(async (): Promise<boolean> => {
    if (hasUnsavedRef.current) {
      return confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        variant: 'warning',
      });
    }
    return true;
  }, [confirm]);

  const handleSelectUser = useCallback(
    async (id: string) => {
      if (!(await confirmUnsaved())) return;
      hasUnsavedRef.current = false;
      setPanel({ mode: 'edit', userId: id });
    },
    [confirmUnsaved],
  );

  const handleClose = useCallback(async () => {
    if (!(await confirmUnsaved())) return;
    hasUnsavedRef.current = false;
    setPanel({ mode: 'closed' });
  }, [confirmUnsaved]);

  const handleUnsavedChange = useCallback((dirty: boolean) => {
    hasUnsavedRef.current = dirty;
  }, []);

  const handleCreateClick = useCallback(async () => {
    if (!(await confirmUnsaved())) return;
    hasUnsavedRef.current = false;
    setPanel({ mode: 'create' });
  }, [confirmUnsaved]);

  const handleCreated = useCallback(() => {
    setPanel({ mode: 'closed' });
  }, []);

  if (!currentUser) return null;

  const panelOpen = panel.mode !== 'closed';
  const selectedUserId = panel.mode === 'edit' ? panel.userId : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        {isAdmin(currentUser.systemRole) && (
          <button
            onClick={handleCreateClick}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <UserPlus size={16} />
            <span className="hidden sm:inline">Create User</span>
          </button>
        )}
      </div>
      <UserFilters filters={filters} onFiltersChange={setFilters} />
      <SplitPanelLayout
        panelOpen={panelOpen}
        onClosePanel={handleClose}
        listContent={
          <UserTable
            users={filteredUsers}
            selectedUserId={selectedUserId}
            onSelectUser={handleSelectUser}
            isLoading={isLoading}
            currentUser={currentUser}
            onDeactivateUser={handleDeactivateUser}
          />
        }
        panelContent={
          panel.mode === 'edit' ? (
            <UserDetailPanel
              key={panel.userId}
              userId={panel.userId}
              onClose={handleClose}
              onUnsavedChange={handleUnsavedChange}
              currentUser={currentUser}
            />
          ) : panel.mode === 'create' ? (
            <CreateUserPanel onClose={handleClose} onCreated={handleCreated} />
          ) : null
        }
      />
    </div>
  );
}
